<template>
  <div>
    <AppHeader />

    <section class="hero hero-eastern reviews-hero">
      <div class="container">
        <div class="hero-content fade-in-up">
          <h1>Отзывы учеников</h1>
          <p>Реальные истории студентов школы «Мама, я в Китае»</p>
        </div>
      </div>
    </section>

    <UISection :narrow="true" class="reviews-section">
      <template #default>
        <div class="state" v-if="loading">Загружаем отзывы…</div>
        <div class="state error" v-else-if="error">{{ error }}</div>
        <div v-else-if="!reviews.length" class="state">Пока нет опубликованных отзывов.</div>
        <template v-else>
          <div class="reviews-grid">
            <UICard class="review-card" v-for="review in paginatedReviews" :key="review.id">
              <div class="review-header">
                <span class="date">{{ formatDate(review.created_at) }}</span>
                <span class="quote-mark" aria-hidden="true">“</span>
              </div>
              <div class="review-body">
                <button
                  v-if="review.media_url"
                  type="button"
                  class="media-preview"
                  aria-label="Открыть фото или видео отзыва"
                  @click="openMedia(review.media_url, review.media_type)"
                >
                  <img v-if="review.media_type === 'image'" :src="review.media_url" alt="Превью" />
                  <div v-else class="video-preview">
                    <video :src="review.media_url" muted></video>
                    <span class="play-icon">▶</span>
                  </div>
                </button>
                <div class="review-text">{{ review.text }}</div>
              </div>
              <div class="review-meta">
                <div class="review-author">{{ review.student_name }}</div>
                <div v-if="review.teacher_name" class="teacher">Преподаватель: {{ review.teacher_name }}</div>
              </div>
            </UICard>
          </div>
          <nav v-if="totalPages > 1" class="pagination" aria-label="Навигация по отзывам">
            <button
              type="button"
              class="pagination-arrow"
              :disabled="currentPage === 1"
              aria-label="Предыдущая страница"
              @click="setPage(currentPage - 1)"
            >←</button>
            <button
              v-for="page in totalPages"
              :key="page"
              type="button"
              class="pagination-page"
              :class="{ active: page === currentPage }"
              :aria-current="page === currentPage ? 'page' : undefined"
              @click="setPage(page)"
            >{{ page }}</button>
            <button
              type="button"
              class="pagination-arrow"
              :disabled="currentPage === totalPages"
              aria-label="Следующая страница"
              @click="setPage(currentPage + 1)"
            >→</button>
          </nav>
        </template>
      </template>
    </UISection>

    <section class="cta-section">
      <div class="container">
        <h2>Хотите, чтобы следующий отзыв был вашим?</h2>
        <p>Запишитесь на бесплатный пробный урок и познакомьтесь с преподавателем</p>
        <UIButton to="/contacts#contact-methods">Записаться на урок</UIButton>
      </div>
    </section>

    <ReviewMediaModal
      v-if="modal.visible"
      :src="modal.src"
      :type="modal.type"
      @close="closeModal"
    />

    <UIFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import UISection from '@/components/ui/UISection.vue'
import UICard from '@/components/ui/UICard.vue'
import UIButton from '@/components/ui/UIButton.vue'
import UIFooter from '@/components/ui/UIFooter.vue'
import ReviewMediaModal from '@/components/ui/ReviewMediaModal.vue'
import axios from 'axios'

const api = axios.create({
  baseURL: '/api/v1'
})

export default {
  name: 'ReviewsPage',
  components: {
    AppHeader,
    UISection,
    UICard,
    UIButton,
    UIFooter,
    ReviewMediaModal
  },
  data() {
    return {
      reviews: [],
      currentPage: 1,
      pageSize: 6,
      loading: false,
      error: '',
      modal: {
        visible: false,
        src: '',
        type: 'image'
      }
    }
  },
  created() {
    this.loadReviews()
  },
  computed: {
    totalPages() {
      return Math.ceil(this.reviews.length / this.pageSize)
    },
    paginatedReviews() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.reviews.slice(start, start + this.pageSize)
    }
  },
  methods: {
    async loadReviews() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await api.get('/public/reviews')
        this.reviews = data || []
        this.currentPage = 1
      } catch (err) {
        this.error = err.response?.data?.detail || 'Не удалось загрузить отзывы'
      } finally {
        this.loading = false
      }
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
    },
    openMedia(src, type) {
      this.modal = {
        visible: true,
        src,
        type: type || 'image'
      }
    },
    closeModal() {
      this.modal.visible = false
      this.modal.src = ''
    },
    setPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return
      this.currentPage = page
      document.querySelector('.reviews-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style scoped>
/* Уменьшаем отступ от hero секции */
.reviews-hero {
  padding-bottom: 40px !important;
}

.reviews-section :deep(.section) {
  padding-top: 40px;
  padding-bottom: 80px;
}

.reviews-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  margin-top: 24px;
}

.review-card:last-child:nth-child(odd) {
  grid-column: 1 / -1;
  width: calc((100% - 24px) / 2);
  justify-self: center;
}

.review-card {
  background: linear-gradient(145deg, #ffffff 0%, #fffaf9 100%);
  backdrop-filter: blur(14px);
  border-radius: 20px;
  border: 1px solid rgba(231, 76, 60, 0.18);
  padding: 28px;
  box-shadow: 0 24px 55px -38px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 260px;
}

.review-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.review-body {
  overflow: hidden;
  flex: 1;
}

.media-preview {
  width: 120px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(229, 231, 235, 0.6);
  border: 0;
  padding: 0;
  cursor: pointer;
  position: relative;
  float: left;
  margin-right: 16px;
  margin-bottom: 8px;
  transition: transform 0.2s ease;
}

.media-preview:hover {
  transform: scale(1.02);
}

.media-preview img,
.media-preview video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-preview {
  position: relative;
  width: 100%;
  height: 100%;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.date {
  color: #64748b;
  font-size: 0.85rem;
}

.quote-mark {
  height: 28px;
  color: rgba(231, 76, 60, 0.24);
  font-family: Georgia, serif;
  font-size: 3.2rem;
  line-height: 1;
}

.review-text {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #2d3748;
  margin: 0;
}

.review-meta {
  display: grid;
  gap: 6px;
  color: #2d3748;
  font-size: 0.95rem;
  padding-top: 16px;
  border-top: 1px solid rgba(226, 232, 240, 0.9);
}

.review-author {
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #e74c3c;
  font-size: 0.8rem;
}

.teacher {
  font-weight: 500;
  color: #475569;
}

.pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 36px;
}

.pagination button {
  min-width: 42px;
  height: 42px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  color: #475569;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.pagination button:hover:not(:disabled):not(.active) {
  border-color: rgba(231, 76, 60, 0.45);
  color: #c0392b;
  transform: translateY(-1px);
}

.pagination button.active {
  border-color: #e74c3c;
  background: #e74c3c;
  color: #fff;
}

.pagination button:disabled {
  cursor: default;
  opacity: 0.4;
}


.state {
  text-align: center;
  color: #475569;
  padding: 24px;
}

.state.error {
  color: #c0392b;
}

@media (max-width: 768px) {
  .reviews-grid {
    grid-template-columns: 1fr;
  }

  .review-card:last-child:nth-child(odd) {
    grid-column: auto;
    width: auto;
  }

  .review-card {
    min-height: 0;
    padding: 24px;
  }

  .media-preview {
    float: none;
    margin-right: 0;
    margin-bottom: 12px;
    width: 100px;
    height: 70px;
  }
  
  .review-body {
    text-align: left;
  }
}
</style>
